import { onMounted, onUnmounted } from 'vue';

import Echo from 'laravel-echo';
import Pusher from 'pusher-js';

declare global {
  interface Window {
    Echo?: Echo<'reverb'>;
    Pusher?: typeof Pusher;
    appConfig?: {
      reverb?: {
        key: string;
        host: string;
        port: number;
        scheme: string;
        path: string;
      };
    };
  }
}

let echoInstance: Echo<'reverb'> | null = null;

export function initEcho(): Echo<'reverb'> | null {
  return ensureEcho();
}

export function destroyEcho(): void {
  if (echoInstance !== null) {
    echoInstance.disconnect();
    echoInstance = null;
    window.Echo = undefined;
  }
}

function ensureEcho(): Echo<'reverb'> | null {
  if (echoInstance !== null) {
    return echoInstance;
  }

  const cfg = window.appConfig?.reverb;
  if (cfg === undefined || cfg.key === '') {
    return null;
  }

  window.Pusher = Pusher;

  echoInstance = new Echo({
    broadcaster: 'reverb',
    key: cfg.key,
    wsHost: cfg.host,
    wsPath: cfg.path,
    wsPort: cfg.port,
    wssPort: cfg.port,
    forceTLS: cfg.scheme === 'https',
    enabledTransports: ['ws', 'wss'],
  });

  window.Echo = echoInstance;
  return echoInstance;
}

export type EchoEventHandler = (payload: Record<string, unknown>) => void;

export interface UseEchoOptions {
  private?: boolean;
}

export function useEcho(
  channelName: string,
  events: Record<string, EchoEventHandler>,
  options: UseEchoOptions = {},
): void {
  onMounted(() => {
    const echo = ensureEcho();
    if (echo === null) {
      return;
    }

    const channel = options.private === true ? echo.private(channelName) : echo.channel(channelName);

    for (const [eventName, handler] of Object.entries(events)) {
      channel.listen(eventName, handler as (data: unknown) => void);
    }
  });

  onUnmounted(() => {
    const echo = window.Echo;
    if (echo !== undefined) {
      echo.leave(channelName);
    }
  });
}
